/**
 * Warranty utility — calculates status from period + purchased_date
 */
export const WARRANTY_PERIODS = [
  { value: 'none', label: 'No Warranty' },
  { value: 'warranty_void', label: 'Warranty Void' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' },
  { value: '2_years', label: '2 Years' },
  { value: '3_years', label: '3 Years' },
  { value: '5_years', label: '5 Years' },
  { value: 'lifetime', label: 'Lifetime' },
];

const PERIOD_MONTHS = { '1_month': 1, '3_months': 3, '6_months': 6 };
const PERIOD_YEARS = { '1_year': 1, '2_years': 2, '3_years': 3, '5_years': 5 };

/**
 * Calculate warranty expiry date
 * Returns Date or null
 */
export function getWarrantyExpiry(purchasedDate, period) {
  if (!purchasedDate || !period || period === 'none') return null;
  if (period === 'lifetime') return null; // never expires
  const d = new Date(purchasedDate);
  const months = PERIOD_MONTHS[period];
  if (months) { d.setMonth(d.getMonth() + months); return d; }
  const years = PERIOD_YEARS[period];
  if (!years) return null;
  d.setFullYear(d.getFullYear() + years);
  return d;
}

/**
 * Returns warranty status object:
 * { status: 'valid'|'expiring'|'expired'|'lifetime'|'none', label, color, expiryDate }
 */
export function getWarrantyStatus(purchasedDate, period) {
  if (!period || period === 'none') {
    return { status: 'none', label: 'No Warranty', color: '#334155' };
  }
  if (period === 'warranty_void') {
    return { status: 'void', label: 'Warranty Void', color: '#64748b' };
  }
  if (period === 'lifetime') {
    return { status: 'lifetime', label: 'Lifetime Warranty', color: '#818cf8' };
  }
  if (!purchasedDate) {
    return { status: 'none', label: `${WARRANTY_PERIODS.find(p=>p.value===period)?.label || period} (no date)`, color: '#334155' };
  }

  const expiry = getWarrantyExpiry(purchasedDate, period);
  if (!expiry) return { status: 'none', label: 'No Warranty', color: '#334155' };

  const now = new Date();
  const daysLeft = Math.floor((expiry - now) / 86400000);

  if (daysLeft < 0) {
    return { status: 'expired', label: `Expired ${Math.abs(daysLeft)}d ago`, color: '#ef4444', expiryDate: expiry.toISOString().split('T')[0] };
  }
  if (daysLeft <= 30) {
    return { status: 'expiring', label: `Expiring in ${daysLeft}d`, color: '#f59e0b', expiryDate: expiry.toISOString().split('T')[0] };
  }
  if (daysLeft <= 90) {
    return { status: 'expiring_soon', label: `${Math.round(daysLeft/30)}mo left`, color: '#fbbf24', expiryDate: expiry.toISOString().split('T')[0] };
  }

  const mths = Math.floor(daysLeft / 30);
  const label = mths >= 12 ? `${Math.floor(mths/12)}y ${mths%12}mo left` : `${mths}mo left`;
  return { status: 'valid', label, color: '#10b981', expiryDate: expiry.toISOString().split('T')[0] };
}
